import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttTopControls,
    SttTable,
    SttPage,
} from '../../../sporttia/all';
import { useToggle } from '../../../lib/hooks';
import { paths, getPath } from '../../Pages';
import { usePermission } from '../../../lib/usePermission';
import { PERMISSIONS } from '../../../lib/permissions';

type Remittence = {
    id: number;
    name: string;
    status: string;
    issueDate: string;
    downloadedDate?: string;
    total: number;
};

type RemittencesResponse = {
    rows: Remittence[];
    count: number;
    page: number;
    pages: number;
};

export default function Remittences(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { can } = usePermission();
    const canGenerate = can(PERMISSIONS.REMITTANCE.CREATE);
    const navigate = useNavigate();
    const [remittences, setRemittences] = useState<RemittencesResponse>();
    const [filters, setFilters] = useState<{ name: string }>({
        name: '',
    });

    // Set header
    useEffect(() => {
        cxt?.setHeader({
            title: t('Remittences'),
        });
    }, []);

    /**
     * Load remittences
     */
    const loadRemittences = (params = { rows: 20, page: 1 }): void => {
        cxt?.api('GET', `/scs/${Number(cxt?.sc?.id)}/remittences`, {
            params: {
                ...{
                    ...filters,
                    name: filters?.name ? filters.name : undefined,
                },
                ...params,

                trash,
            },
            success: (r: RemittencesResponse) => {
                setRemittences(r);
            },
        });
    };

    // Trash hook
    const [trash, toggleTrash] = useToggle(() => {
        loadRemittences();
    });

    /**
     * Go to generate remittence
     */
    const goToGenerateRemittend = (): void => {
        navigate(paths.find((e: any) => e.id === 'genRemittences')!.path); // '/scs/remittences/generate')
    };

    // -----| Render |-----
    return (
        <SttPage title={t('Remittences')}>
            <SttTopControls
                p={2}
                fields={
                    [
                        {
                            caption: t('Name'),
                            name: 'name',
                            type: 'text',
                            value: filters.name,
                            onEnterKey: loadRemittences,
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 3,
                        },
                    ] as any
                }
                onChange={({
                    name,
                    value,
                }: {
                    name: string | undefined;
                    value: unknown;
                }) => {
                    if (!name) return;
                    setFilters({ ...filters, [name]: value as string });
                }}
                onFilter={loadRemittences}
                onToggleTrash={toggleTrash}
                mainAction={
                    canGenerate
                        ? {
                              type: 'create',
                              onClick: () => goToGenerateRemittend(),
                          }
                        : undefined
                }
            />

            <SttTable
                data={remittences}
                loading={false}
                columns={[
                    {
                        type: 'link',
                        title: t('Name'),
                        field: 'name',
                        align: 'left' as const,
                        // to: row => `/scs/remittences/${row.id}`,
                        to: (row: Remittence) =>
                            getPath('remittence' as any, { id: row.id }),
                    },
                    {
                        type: 'active',
                        title: t('Sent'),
                        field: 'status',
                        align: 'center' as const,
                        value: (row: Remittence) => row.status === 'SENT',
                    },
                    {
                        type: 'dmy',
                        title: t('Paid'),
                        field: 'issueDate',
                        align: 'center' as const,
                    },
                    {
                        type: 'dmy',
                        title: t('downloaded'),
                        field: 'downloadedDate',
                        align: 'center' as const,
                    },
                    {
                        type: 'price',
                        title: t('Total'),
                        field: 'total',
                        align: 'right' as const,
                    },
                ]}
                onFetch={loadRemittences}
            />
        </SttPage>
    );
}
